import React from "react";
import { Link } from "react-router-dom";
import { Star, MapPin, Phone } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { resolveImageUrl } from "@/components/forms/ImageUploader";
import SpaBadges from "./SpaBadges";

const FALLBACK = "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=1200&q=80";

export function SpaCardGrid({ spa }) {
  const cover = resolveImageUrl(spa.cover_image || (spa.gallery && spa.gallery[0])) || FALLBACK;
  return (
    <Link
      to={`/spa/${spa.slug}`}
      data-testid="spa-card-grid"
      className={`group block rounded-2xl bg-white overflow-hidden card-lift shadow-[0_8px_24px_rgba(17,24,39,0.06)] hover:shadow-[0_16px_44px_rgba(17,24,39,0.12)] border ${spa.featured ? "border-[rgba(201,168,106,0.4)]" : "border-[rgba(17,24,39,0.08)]"}`}
    >
      <AspectRatio ratio={4 / 3} className="bg-[#F5EFE6] overflow-hidden">
        <img
          src={cover}
          alt={spa.spa_name}
          loading="lazy"
          onError={(e) => {
            // Owner-provided cover URLs occasionally 404 (e.g. removed/renamed).
            // Fall back once to a safe stock image so the card never looks empty.
            if (e.currentTarget.src !== FALLBACK) e.currentTarget.src = FALLBACK;
          }}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </AspectRatio>
      <div className="p-4">
        <div className="mb-2"><SpaBadges featured={spa.featured} premium={spa.premium} verified={spa.verified} /></div>
        <h3 className="font-heading text-lg text-[#111827] line-clamp-1" data-testid="spa-card-name">{spa.spa_name}</h3>
        <div className="flex items-center gap-1 text-xs text-[#4B5563] mt-1">
          <MapPin size={12} /> {spa.locality}, {spa.city || "Pune"}
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-1.5 text-sm">
            <Star size={14} className="fill-[#C9A86A] text-[#C9A86A]" />
            <span className="font-medium text-[#111827]">{spa.rating?.toFixed(1) || "4.5"}</span>
            <span className="text-[#4B5563]">· {spa.category}</span>
          </div>
          <span className="text-sm text-[#1F4E3D] font-medium">{spa.price_range || "₹₹"}</span>
        </div>
      </div>
    </Link>
  );
}

export function SpaCardList({ spa }) {
  const cover = resolveImageUrl(spa.cover_image || (spa.gallery && spa.gallery[0])) || FALLBACK;
  return (
    <Link
      to={`/spa/${spa.slug}`}
      data-testid="spa-card-list"
      className="group flex gap-4 p-3 rounded-2xl bg-white border border-[rgba(17,24,39,0.08)] hover:shadow-[0_12px_30px_rgba(17,24,39,0.08)] transition-shadow"
    >
      <div className="w-32 sm:w-48 flex-shrink-0">
        <AspectRatio ratio={1} className="bg-[#F5EFE6] overflow-hidden rounded-xl">
          <img
            src={cover}
            alt={spa.spa_name}
            loading="lazy"
            onError={(e) => {
              if (e.currentTarget.src !== FALLBACK) e.currentTarget.src = FALLBACK;
            }}
            className="w-full h-full object-cover"
          />
        </AspectRatio>
      </div>
      <div className="flex-1 min-w-0 py-1">
        <SpaBadges featured={spa.featured} premium={spa.premium} verified={spa.verified} />
        <h3 className="font-heading text-lg mt-1.5 text-[#111827] line-clamp-1">{spa.spa_name}</h3>
        <div className="flex items-center gap-1 text-xs text-[#4B5563] mt-1">
          <MapPin size={12} /> {spa.locality}, {spa.city || "Pune"}
        </div>
        <p className="text-sm text-[#4B5563] mt-2 line-clamp-2">{spa.description}</p>
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-1.5 text-sm">
            <Star size={14} className="fill-[#C9A86A] text-[#C9A86A]" />
            <span className="font-medium">{spa.rating?.toFixed(1) || "4.5"}</span>
            <span className="text-[#4B5563] hidden sm:inline">· {spa.category}</span>
          </div>
          <span className="text-sm text-[#1F4E3D] font-medium hidden sm:block">{spa.price_range}</span>
        </div>
      </div>
    </Link>
  );
}
